import { IntlShape } from 'react-intl';
import { messages } from '../../i18n';
import {
  LOCALIZED_PATHS,
  localizeRoute,
  delocalizeRoute,
  JOBS_LISTING_SEGMENT,
  JOBS_LISTING_SEGMENTS as JOBS_LISTING_SEGMENTS_LIST,
} from '../constants/url-constants';
import { normalizeFilterKey, normalizeFilterValue } from '../facets/normalize-filter-value';
import { buildEncodedLocationSrpPathSegment } from '../location/build-encoded-location-srp-path-segment';
import { decodeLocationSlug } from '../location/decode-location-slug';
import {
  decodeOpaqueUrlPathSegment,
  encodeOpaqueUrlPathSegment,
} from '../routing/encode-opaque-url-path-segment';
import { companyProfileLogicalPath } from '../routing/company-profile-url-path';
import { isSummerJobsResourcePathname } from '../routing/summer-jobs-resource-paths';
import {
  inferAppLocaleFromPathname,
  summerJobTypeFacetForLocale,
} from '../job/summer-job-job-type';
import { freelanceJobTypeFacetForLocale } from '../job/freelance-job-type';
import { WORK_MODEL_SEGMENTS, workModelFacetValue, workModelForRemotenessRefinement, workModelForSegment, workModelSegment } from '../work-model/work-model-hub';
import { parseAlgoliaNamespacedParamKey } from './parse-namespaced-search-param';
import { KNOWN_FACET_ATTRIBUTE_NAMES } from '../typesense/collection-schemas';
import { isSrpNonFilterQueryParam } from '../search/srp-non-filter-query-params';
import { getCategoryBySlug, getCategoryFieldValuesForLocale } from '../category/category-hub-config';
import { getRoleBySlug, isRolePathMarker, titleValuesMatchRole } from '../role/role-hub-config';
import { SRP_HITS_PER_PAGE, isValidSrpHitsPerPage } from '../srp/srp-hits-per-page';

const EXCLUDE_FACET_PREFIX = 'exclude_';
const ONLY_FACET_PREFIX = 'only_';

/** True for a real Typesense facet attribute, an `exclude_<facet>` negative-filter key, or an
 * `only_<facet>` exact-match key (see build-alert-search-params.ts) wrapping one. Anything else
 * isn't a filterable field and must not reach filter_by (a single bad param would 400 the whole
 * search/alert-match query). */
function isKnownFacetKey(key: string): boolean {
  const bare = key.startsWith(EXCLUDE_FACET_PREFIX)
    ? key.slice(EXCLUDE_FACET_PREFIX.length)
    : key.startsWith(ONLY_FACET_PREFIX)
      ? key.slice(ONLY_FACET_PREFIX.length)
      : key;
  // `firstSeenMin` (and its deprecated alias `lastSeenMin`) is a virtual/UI-level facet key (the
  // "new jobs" first-seen threshold filter): it maps to the real Typesense field `firstSeen` via
  // refinementKeyToAlgoliaNumericAttribute rather than sharing that field's name, so it never
  // appears in KNOWN_FACET_ATTRIBUTE_NAMES (built from raw schema field names) and was silently
  // dropped here before reaching generateUrlFromIndexUiState/build-typesense-search-params, both of
  // which already handle it by this exact name. Confirmed via a direct
  // parseUrlToIndexUiState('/en/jobs?lastSeenMin=...') probe that this dropped the filter on any
  // fresh navigation to such a URL (e.g. a bookmarked or externally-linked "new in the last 24h"
  // link), even though the app already builds URLs shaped exactly like this (AllFiltersModal's
  // "this week" quick filter, the navbar live-index "new today" figure).
  if (bare === 'firstSeenMin' || bare === 'lastSeenMin') return true;
  return KNOWN_FACET_ATTRIBUTE_NAMES.has(bare);
}

/**
 * Localized path segment after /jobs for the company facet (mirrors location > alue > omrade).
 * Same word as the employer profile segment so `/fi/tyopaikat/tyonantaja/{id}` and
 * `/fi/tyonantaja/{slug}` read as one entity. The value after it is still the raw companyId
 * (the facet value), not the profile slug: the SRP filter has to round-trip client-side without
 * a lookup, and this page canonicalises to the profile anyway.
 */
export const JOBS_COMPANY_PATH_SEGMENT: Record<'en' | 'fi' | 'sv', string> = {
  en: 'employer',
  fi: 'tyonantaja',
  sv: 'arbetsgivare',
};

/** Previous company-facet markers, still parsed so old links resolve (url-rewrite-config 301s them). */
export const JOBS_COMPANY_LEGACY_PATH_SEGMENTS: readonly string[] = ['company', 'yritys', 'foretag'];

/** Path marker word for the category-hub segment (`/jobs/category/{slug}`), per locale. */
const CATEGORY_PATH_MARKER_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'category',
  fi: 'kategoria',
  sv: 'kategori',
};
const CATEGORY_PATH_MARKERS = new Set(Object.values(CATEGORY_PATH_MARKER_BY_LOCALE));

/** Role-hub path marker per locale (mirrors role-hub-config.ts's own, non-exported map). */
const ROLE_PATH_MARKER_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'role',
  fi: 'ammatti',
  sv: 'yrke',
};

/** Location-facet path marker per locale, plus legacy diacritic/encoded variants for sv. */
const LOCATION_PATH_MARKER_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'location',
  fi: 'alue',
  sv: 'omrade',
};
const LOCATION_PATH_MARKERS = new Set(['location', 'alue', 'omrade', 'område', 'omr%C3%A5de']);

/** Freelance-hub path marker per locale (`/jobs/freelance`, fi `keikkatyo`, sv `frilans`). */
const FREELANCE_PATH_MARKER_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'freelance',
  fi: 'keikkatyo',
  sv: 'frilans',
};
const FREELANCE_PATH_MARKERS = new Set(Object.values(FREELANCE_PATH_MARKER_BY_LOCALE));

/** Every locale's roles-index segment (`/jobs/roles`, fi `ammatit`, sv `yrken`). */
const ROLES_INDEX_SEGMENTS = new Set(Object.values(LOCALIZED_PATHS).map((paths) => paths.rolesIndex));

export interface GenerateUrlFromIndexUiStateOptions {
  /**
   * When true, never emit `/{locale}/company/{id}` (employer profile).
   * Use for links that must stay on the jobs listing with a path-encoded company (e.g. “See all jobs”).
   */
  forceJobsListingForCompany?: boolean;
}

// Sourced from the shared JOBS_LISTING_SEGMENT(S) in url-constants.ts rather than hand-rolled:
// sv's listing segment ('lediga-jobb') is NOT its job-DETAIL segment ('jobb',
// LOCALIZED_PATHS.sv.job). Including 'jobb' here once made every sv job detail page look like
// an SRP path to this file's callers (parseUrlToIndexUiState / pathnameIsJobsListingPath),
// which fed into URL-generation logic and rewrote real job URLs to a nonexistent
// /sv/lediga-jobb/{jobId}/... shape (2026-09-02 — same root cause class as the fix in
// url-rewrite-config.ts).
const JOBS_LISTING_SEGMENTS = new Set(JOBS_LISTING_SEGMENTS_LIST);

/**
 * True when the pathname includes a localized jobs listing segment (e.g. `/en/jobs`, `/fi/tyopaikat/yritys/…`).
 * Used when syncing search URL state so we do not collapse sole `companyId` filters to employer profile URLs
 * while the user is on the SRP — that would replace `/en/jobs/company/x` with `/en/company/x`, which does not
 * match the index route and breaks filters and navigation.
 */
export function pathnameIsJobsListingPath(pathname: string): boolean {
  const raw = pathname.split('?')[0] || '';
  const segments = raw.split('/').filter(Boolean);
  return segments.some((s) => JOBS_LISTING_SEGMENTS.has(s.toLowerCase()));
}

/** True when pathname is an employer profile route (`/fi/tyonantaja/slug`, `/sv/arbetsgivare/slug`, `/en/employer/slug`, plus the legacy `company`/`yritys` segments). */
export function isCompanyProfilePath(pathnameOrHref: string): boolean {
  const path = pathnameOrHref.split('?')[0] || '';
  return /^\/(?:en|fi|sv)\/(?:employer|tyonantaja|arbetsgivare|company|yritys)\/[^/]+$/i.test(path);
}

function countRefinementAttributes(ref: Record<string, string[] | undefined> | undefined, exclude: Set<string>): number {
  if (!ref) return 0;
  let n = 0;
  for (const [k, v] of Object.entries(ref)) {
    if (exclude.has(k)) continue;
    const arr = Array.isArray(v) ? v : [];
    if (arr.some((x) => x != null && String(x).trim() !== '')) n++;
  }
  return n;
}

export interface IndexUiState {
  [key: string]: any;
  query?: string;
  page?: number;
  /** Results per SRP page (see srp-hits-per-page.ts). Stored as URL param `perPage`. */
  perPage?: number;
  sort?: 'relevance' | 'date' | 'salary';
  /** When false, show jobs globally; when true or omitted, show Finland-only. Stored as URL param `finland`. */
  isFinland?: boolean;
  /** SRP view mode override. Only 'map' is ever set explicitly; 'list' (the default) is represented by omission. Stored as URL param `view`. */
  view?: 'list' | 'map';
  refinementList?: {
    [attribute: string]: string[];
  };
  // Add other UI state properties as needed
}

/**
 * Parses a URL pathname into an IndexUiState object
 * @param pathname The URL pathname to parse
 * @returns An IndexUiState object containing the parsed parameters
 */
// SIMPLE VERSION FOR DEBUGGING
export function parseUrlToIndexUiState(pathname: string): IndexUiState {
  const isServer = typeof window === 'undefined';
  
  // Simple: just parse query parameters, ignore path structure
  let urlObj: URL | null = null;
  try {
    urlObj = new URL(pathname, 'http://localhost');
  } catch {}
  const searchParams = urlObj ? urlObj.searchParams : new URLSearchParams(pathname.split('?')[1] || '');
  const pathForLocaleEarly = urlObj ? urlObj.pathname : pathname.split('?')[0] || '/';
  const appLocale = inferAppLocaleFromPathname(pathForLocaleEarly);

  const state: IndexUiState = {};
  
  // Parse basic query parameters
  const refinementList: Record<string, string[]> = {};
  
  // ADDING BACK: Extract location from path - handle multiple URL patterns like route.ts
  try {
    const rawPath = urlObj ? urlObj.pathname : pathname.split('?')[0] || '/';
    const segs = rawPath.split('/').filter(Boolean);
    
    // Determine if first segment is a locale
    const firstSegment = segs[0]?.toLowerCase();
    const isFirstSegmentLocale = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);

    // Standalone employer profile: /{locale}/…/slug (logical path /company/{slug})
    if (isFirstSegmentLocale && segs.length === 3) {
      const withoutLocale = `/${segs[1]}/${segs[2]}`;
      const loc = firstSegment as keyof typeof LOCALIZED_PATHS;
      const deloc = delocalizeRoute(withoutLocale, loc);
      const m = deloc.match(/^\/company\/([^/]+)$/);
      if (m) {
        refinementList.companyId = [
          normalizeFilterValue('companyId', decodeURIComponent(m[1]), appLocale),
        ];
      }
    }

    // Find jobs segment index (accounting for optional locale prefix)
    // 'jobb' intentionally excluded — see JOBS_LISTING_SEGMENTS comment above.
    const jobsIdx = segs.findIndex((s) => JOBS_LISTING_SEGMENTS.has(s));
    
    if (jobsIdx !== -1) {
      // Work model marker (etatyo / remote / distans, hybridityo / hybrid / hybridjobb) sits
      // right after the jobs segment and is a `remoteness` refinement; every marker below is
      // read relative to it.
      let afterJobs = jobsIdx + 1;
      const workModel = workModelForSegment(segs[afterJobs]);
      if (workModel) {
        refinementList.remoteness = [workModelFacetValue(workModel, appLocale)];
        afterJobs += 1;
      }
      const seg1 = segs[afterJobs];

      if (
        seg1 &&
        (seg1 === 'location' ||
          seg1 === 'alue' ||
          seg1 === 'omrade' ||
          seg1 === 'område' ||
          seg1 === 'omr%C3%A5de')
      ) {
        const locSeg = segs[afterJobs + 1];
        if (locSeg) {
          const tokens = locSeg.split('+');
          const decodedList = tokens.map((t) => decodeLocationSlug(t)).filter(Boolean);
          if (decodedList.length > 0) {
            refinementList.location = decodedList;
          }
        }
      }

      // Bare /jobs/freelance (fi: keikkatyo, sv: frilans) — expands to the same jobType
      // facet value the "Hide Freelance Postings" exclude toggle already targets.
      if (seg1 && ['freelance', 'keikkatyo', 'frilans'].includes(seg1.toLowerCase())) {
        refinementList.jobType = [freelanceJobTypeFacetForLocale(appLocale)];
      }

      // Optional /jobs/.../company/{slug} (after optional /location/… segment)
      let cursor = afterJobs;
      const marker = segs[cursor];
      if (
        marker &&
        (marker === 'location' ||
          marker === 'alue' ||
          marker === 'omrade' ||
          marker === 'område' ||
          marker === 'omr%C3%A5de')
      ) {
        cursor += 2;
      }
      const companyMarker = segs[cursor];
      const companySlug = segs[cursor + 1];
      if (
        companyMarker &&
        companySlug &&
        [...Object.values(JOBS_COMPANY_PATH_SEGMENT), ...JOBS_COMPANY_LEGACY_PATH_SEGMENTS].includes(companyMarker.toLowerCase())
      ) {
        refinementList.companyId = [
          normalizeFilterValue('companyId', decodeOpaqueUrlPathSegment(companySlug), appLocale),
        ];
        cursor += 2;
      }

      // Optional /jobs/.../category/{slug} (after optional /location/… and /company/… segments).
      // Category slugs are noisy `field` facet buckets — resolve through the curated config so
      // every spelling variant for the category is OR-filtered together.
      const categoryMarker = segs[cursor];
      const categorySlug = segs[cursor + 1];
      if (
        categoryMarker &&
        categorySlug &&
        ['category', 'kategoria', 'kategori'].includes(categoryMarker.toLowerCase())
      ) {
        const category = getCategoryBySlug(decodeURIComponent(categorySlug));
        if (category) {
          refinementList.field = getCategoryFieldValuesForLocale(category, appLocale);
        }
        cursor += 2;
      }

      // Optional /jobs/.../role/{slug} (fi: ammatti, sv: yrke). A role is a normalisedTitle
      // filter (the LLM-normalised job title), not a sector `field` filter. Values are
      // per-locale: each Typesense collection stores normalisedTitle in its own language.
      const roleMarker = segs[cursor];
      const roleSlug = segs[cursor + 1];
      if (roleMarker && roleSlug && isRolePathMarker(roleMarker)) {
        const roleDef = getRoleBySlug(decodeURIComponent(roleSlug));
        if (roleDef) {
          refinementList.normalisedTitle = roleDef.titleValues[appLocale] ?? roleDef.titleValues.en;
        }
      }
    }
  } catch (error) {
    // Silently handle path parsing errors
  }
  
  // List of tracking/marketing parameters to ignore (not treated as search filters)
  const ignoredParams = new Set([
    // UTM parameters (Google Ads, marketing campaigns)
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    // Google Click ID
    'gclid',
    // Google Browser ID (used by Google crawlers)
    'gbraid',
    'wbraid',
    // Facebook Click ID
    'fbclid',
    // Microsoft Click ID
    'msclkid',
    // Other common tracking parameters
    'ref',
    'source',
    'medium',
    'campaign',
    'term',
    'content',
    // Google Analytics
    '_ga',
    '_gid',
    // Other analytics/tracking
    'mc_cid',
    'mc_eid',
    // Auth / navigation (not part of job search state)
    'returnTo',
    'utm_id',
    'utm_source_platform',
    'utm_creative_format',
    'utm_marketing_tactic',
  ]);

  for (const [key, value] of searchParams.entries()) {
    // Normalize parameter key to lowercase for case-insensitive matching
    const normalizedKey = normalizeFilterKey(key);

    // Skip ignored tracking parameters and app-only query keys (e.g. search_provider)
    if (ignoredParams.has(normalizedKey) || isSrpNonFilterQueryParam(key)) {
      continue;
    }

    // Legacy InstantSearch namespaced keys (e.g. sv_sok[refinementList][remoteness][0]=...)
    // must map to real facet attributes; otherwise Algolia gets impossible filters and returns 0 hits.
    const namespaced = parseAlgoliaNamespacedParamKey(key);
    if (namespaced.kind === 'query') {
      state.query = value;
      continue;
    }
    if (namespaced.kind === 'page') {
      const pageNum = parseInt(value, 10);
      if (!isNaN(pageNum)) {
        state.page = pageNum;
      }
      continue;
    }
    if (namespaced.kind === 'sort') {
      continue;
    }
    if (namespaced.kind === 'refinement') {
      // Deprecated `lastSeenMin` is transparently upgraded to the canonical `firstSeenMin` so the
      // rest of the app reads one key (see refinementKeyToAlgoliaNumericAttribute).
      const filterKey = namespaced.attribute === 'lastSeenMin' ? 'firstSeenMin' : namespaced.attribute;
      // Not a real Typesense facet (stale/renamed field, hand-crafted URL, etc.) — forwarding
      // it to filter_by would 400 the whole query. Drop just this one refinement instead.
      if (!isKnownFacetKey(filterKey)) {
        continue;
      }
      if (!refinementList[filterKey]) {
        refinementList[filterKey] = [];
      }
      refinementList[filterKey].push(normalizeFilterValue(filterKey, value, appLocale));
      continue;
    }

    if (normalizedKey === 'query' || normalizedKey === 'search') {
      state.query = value;
    } else if (normalizedKey === 'page') {
      const pageNum = parseInt(value, 10);
      if (!isNaN(pageNum)) {
        state.page = pageNum;
      }
    } else if (normalizedKey === 'sort') {
      if (value === 'relevance' || value === 'date' || value === 'salary') {
        state.sort = value;
      }
    } else if (normalizedKey === 'perpage') {
      const perPageNum = parseInt(value, 10);
      if (isValidSrpHitsPerPage(perPageNum)) {
        state.perPage = perPageNum;
      }
    } else if (normalizedKey === 'finland' || normalizedKey === 'isfinland') {
      state.isFinland = value === '1' || value === 'true';
    } else if (normalizedKey === 'view') {
      // Only an explicit 'map' is treated as an override; any other/missing value means
      // "no override" rather than an explicit 'list', so cookie/localStorage prefs can still apply.
      if (value === 'map') {
        state.view = 'map';
      }
    } else if (key.includes('[sort]') || key.endsWith('[sort]')) {
      // Skip old format sort parameters like "en_search[sort]"
      // These are legacy parameters that should be ignored
      continue;
    } else if (isKnownFacetKey(key)) {
      // Treat other parameters as filters — use flat key as Algolia attribute (e.g. jobType, remoteness).
      // Deprecated `lastSeenMin` is transparently upgraded to `firstSeenMin` (see the namespaced
      // branch above).
      const filterKey = key === 'lastSeenMin' ? 'firstSeenMin' : key;
      if (!refinementList[filterKey]) {
        refinementList[filterKey] = [];
      }
      refinementList[filterKey].push(normalizeFilterValue(filterKey, value, appLocale));
    }
    // Any other unrecognized key (not a real Typesense facet — stale/renamed field, hand-typed
    // URL, unrelated query param) is silently dropped rather than forwarded to filter_by, which
    // would 400 the entire search/alert-match query over a single bad param.
  }

  // Legacy URLs used ?isSummerJob=true — map to locale-specific jobType facet
  const pathForLocale = pathForLocaleEarly;
  const summerFlags = refinementList.isSummerJob;
  if (summerFlags?.some((v) => v === 'true' || v === '1')) {
    delete refinementList.isSummerJob;
    const loc = inferAppLocaleFromPathname(pathForLocale);
    const facet = summerJobTypeFacetForLocale(loc);
    if (!refinementList.jobType) refinementList.jobType = [];
    if (!refinementList.jobType.includes(facet)) {
      refinementList.jobType.push(facet);
    }
  }
  
  // Add refinementList if we have any filters
  if (Object.keys(refinementList).length > 0) {
    state.refinementList = refinementList;
  }
  
  return state;
}

// SIMPLE VERSION FOR DEBUGGING
export function generateUrlFromIndexUiState(
  indexUiState: IndexUiState,
  indexName: string,
  originalPath?: string,
  options?: GenerateUrlFromIndexUiStateOptions,
): string {
  const params = new URLSearchParams();
  // Infer locale from index name like "en_search" | "fi_hae" | "sv_sok"
  const localeMatch = indexName.match(/^([a-z]{2})[_-]/i);
  const locale = ((localeMatch ? localeMatch[1] : 'en').toLowerCase() as 'en' | 'fi' | 'sv') || 'en';

  const refEarly = indexUiState.refinementList || {};
  const companyIdsEarly = refEarly.companyId || [];
  const soleCompanyId =
    companyIdsEarly.length === 1 && String(companyIdsEarly[0] ?? '').trim()
      ? String(companyIdsEarly[0]).trim()
      : '';
  const locEarly = refEarly.location || [];
  const hasQueryEarly = Boolean(indexUiState.query && indexUiState.query.trim().length > 0);
  const hasSortEarly = Boolean(indexUiState.sort && indexUiState.sort !== 'relevance');
  const hasPageEarly =
    indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0;
  const hasPerPageEarly =
    indexUiState.perPage !== undefined && indexUiState.perPage !== null && indexUiState.perPage !== SRP_HITS_PER_PAGE;
  const otherRefinementCount = countRefinementAttributes(
    refEarly as Record<string, string[] | undefined>,
    new Set(['companyId', 'location']),
  );

  if (
    soleCompanyId &&
    !options?.forceJobsListingForCompany &&
    locEarly.length === 0 &&
    !hasQueryEarly &&
    !hasSortEarly &&
    !hasPageEarly &&
    !hasPerPageEarly &&
    otherRefinementCount === 0
  ) {
    const logicalProfile = companyProfileLogicalPath(soleCompanyId);
    const base = `/${locale}${localizeRoute(logicalProfile, locale)}`;
    const finOnly = new URLSearchParams();
    if (indexUiState.isFinland === false) finOnly.set('finland', '0');
    const q = finOnly.toString();
    return q ? `${base}?${q}` : base;
  }

  // Get localized job segment
  const jobSegment = JOBS_LISTING_SEGMENT[locale as keyof typeof JOBS_LISTING_SEGMENT] || 'jobs';
  // The server rewrites localized SRP paths to their logical form before Vike sees them, so on
  // fi/sv the path handed in here can be `/fi/jobs/location/tampere_finland` rather than
  // `/fi/tyopaikat/alue/tampere_suomi`. Fold it back to the localized base first, dropping any
  // location marker of any locale; the refinement list below re-adds the right one. Without
  // this, pagination links came out as `/fi/jobs/location/tampere_finland/alue/tampere_suomi`.
  if (originalPath && locale !== 'en') {
    const [pathPart, queryPart] = originalPath.split('?');
    let normalized = pathPart.replace(new RegExp(`^/${locale}/jobs(?=/|$)`), `/${locale}/${jobSegment}`);
    if (normalized !== pathPart) {
      normalized = normalized.replace(/\/(?:location|alue|omrade|omr%C3%A5de|område)\/[^/?]+/gi, '');
      originalPath = queryPart !== undefined ? `${normalized}?${queryPart}` : normalized;
    }
  }
  // Get localized location segment
  const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' } as const;
  const locationSegment = (locationSegments as any)[locale] || 'location';
  const jobsCompanySegment = JOBS_COMPANY_PATH_SEGMENT[locale] || 'company';

  // Check if we have any search parameters (query, filters, sort, page > 0)
  const hasQuery = indexUiState.query && indexUiState.query.trim().length > 0;
  const hasFilters = indexUiState.refinementList && Object.keys(indexUiState.refinementList).length > 0;
  const hasSort = indexUiState.sort && indexUiState.sort !== 'relevance';
  const hasPage = indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0;
  const hasPerPage =
    indexUiState.perPage !== undefined && indexUiState.perPage !== null && indexUiState.perPage !== SRP_HITS_PER_PAGE;

  // Only add /jobs segment if we have search parameters AND the original path doesn't already have a job segment
  // If no search parameters and we have an original path, preserve it
  let path = '';
  if (hasQuery || hasFilters || hasSort || hasPage || hasPerPage) {
    // Check if originalPath already has a job segment
    const hasJobSegment =
      originalPath &&
      (originalPath.includes('/jobs') ||
        originalPath.includes('/tyopaikat') ||
        originalPath.includes('/lediga-jobb') ||
        originalPath.includes('/jobb'));

    const preserveResourceLanding =
      originalPath && isSummerJobsResourcePathname(originalPath);
    
    if (hasJobSegment || preserveResourceLanding) {
      // Preserve the original path structure if it already has a job segment
      path = originalPath;
    } else {
      // Add the job segment if the original path doesn't have one
      path = `/${locale}/${jobSegment}`;
    }
  } else if (originalPath) {
    // English marketing home is canonical at `/`; preserve other locale roots and paths.
    path =
      locale === 'en' && (originalPath === '/en' || originalPath === '/en/')
        ? '/'
        : originalPath;
  } else {
    // Fallback to default behavior if no original path provided
    path = locale === 'en' ? '/' : `/${locale}`;
  }

  // Work model in path: a sole remoteness refinement that is the locale's own remote/hybrid
  // facet value is always encoded as the segment right after the jobs segment
  // (/tyopaikat/etatyo, /jobs/hybrid). Unlike the freelance segment this is proactive: the
  // remote hub is the one indexable URL for that filter, and the robots layer marks the
  // `?remoteness=` form noindex, so the client must never produce it for a hub-shaped state.
  const workModelSegmentRe = new RegExp(`/(?:${WORK_MODEL_SEGMENTS.join('|')})(?=/|\\?|$)`, 'i');
  const encodedWorkModel = workModelForRemotenessRefinement(indexUiState.refinementList?.remoteness, locale);
  let remotenessEncodedInPath = false;
  {
    const [pathPart, queryPart] = path.split('?');
    const jobsSegmentRe = /^(\/(?:en|fi|sv))?\/(jobs|tyopaikat|lediga-jobb|jobb)(?=\/|$)/i;
    let cleaned = pathPart.replace(workModelSegmentRe, '');
    if (encodedWorkModel) {
      const seg = workModelSegment(encodedWorkModel, locale);
      if (jobsSegmentRe.test(cleaned)) {
        cleaned = cleaned.replace(jobsSegmentRe, `$&/${seg}`);
      } else {
        cleaned = `/${locale}/${jobSegment}/${seg}`;
      }
      remotenessEncodedInPath = true;
    }
    path = queryPart !== undefined ? `${cleaned}?${queryPart}` : cleaned;
  }

  // Location in path: every selected location is encoded, joined by '+' (mirrors the parser at
  // the top of this file, which splits a location path segment on '+' back into
  // refinementList.location). This used to encode only locValues[0], silently dropping every
  // location added after the first: clicking a second location updated state, but the very next
  // URL write (SearchContext's replaceState -> urlchange -> re-parse) reverted it back down to
  // one, so the click appeared to do nothing.
  const locValues = indexUiState.refinementList?.location || [];
  
  // Check if the path already has a location segment
  const hasLocationInPath = originalPath && (
    originalPath.includes('/location/') ||
    originalPath.includes('/alue/') ||
    originalPath.includes('/omrade/') ||
    originalPath.includes('/område/') ||
    originalPath.includes('/omr%C3%A5de/')
  );
  
  if (locValues && locValues.length > 0) {
    // Encode every selected location and join with '+', so a second (third, ...) selected
    // location round-trips through the URL instead of being silently dropped.
    const encodedLocs = locValues
      .map((v) => (v ? buildEncodedLocationSrpPathSegment(String(v), locale) : ''))
      .filter(Boolean)
      .join('+');

    if (encodedLocs) {
      // The path grammar is jobs / [workModel] / [location] / [company] / [category] / [role]
      // (pages/index/match-search-index-path.ts), so a location segment goes right after the
      // jobs (and work-model) segment, never at the end: appending it after an existing
      // /ammatti/{role} or /kategoria/{slug} produced canonicals the router does not match.
      const insertLocationAfterPrefix = (p: string): string => {
        const prefixRe = new RegExp(`^((?:/(?:en|fi|sv))?/(?:jobs|tyopaikat|lediga-jobb|jobb)(?:/(?:${WORK_MODEL_SEGMENTS.join('|')}))?)(?=/|\\?|$)`, 'i');
        return prefixRe.test(p) ? p.replace(prefixRe, `$1/${locationSegment}/${encodedLocs}`) : `${p}/${locationSegment}/${encodedLocs}`;
      };
      if (!hasLocationInPath) {
        path = insertLocationAfterPrefix(path);
      } else {
        // Replace existing location segment with updated locations
        const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' };
        const currentLocationSegment = (locationSegments as any)[locale] || 'location';
        
        // Remove existing location segment and add updated one
        // Handle both encoded and unencoded versions
        const encodedLocationSegment =
          currentLocationSegment === 'omrade' ? 'omrade' : currentLocationSegment;
        const legacyEncodedSegment =
          currentLocationSegment === 'omrade' ? 'omr%C3%A5de' : currentLocationSegment;
        const pathWithoutLocation = path.replace(new RegExp(`/(${currentLocationSegment}|${encodedLocationSegment}|location|alue|omrade)/[^/?]+`, 'i'), '');
        const legacyCleaned = pathWithoutLocation.replace(
          new RegExp(`/(${legacyEncodedSegment})/[^/?]+`),
          ''
        );
        path = insertLocationAfterPrefix(legacyCleaned);
      }
    }
  } else if (hasLocationInPath) {
    // No location values but path has location segment - remove it
    const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' };
    const currentLocationSegment = (locationSegments as any)[locale] || 'location';
    
    // Remove existing location segment
    // Handle both encoded and unencoded versions
    const encodedLocationSegment =
      currentLocationSegment === 'omrade' ? 'omrade' : currentLocationSegment;
    const legacyEncodedSegment =
      currentLocationSegment === 'omrade' ? 'omr%C3%A5de' : currentLocationSegment;
    path = path
      .replace(new RegExp(`/(${currentLocationSegment}|${encodedLocationSegment}|location|alue|omrade)/[^/?]+`, 'i'), '')
      .replace(new RegExp(`/(?:${legacyEncodedSegment})/[^/?]+`), '');
  }

  // Company in path (after optional location), same idea as location — never use ?companyId=
  const companyIdsForPath = indexUiState.refinementList?.companyId || [];
  const hasCompanyInJobsPath =
    originalPath &&
    /\/(?:employer|tyonantaja|arbetsgivare|company|yritys|foretag)\/[^/?]+/i.test(originalPath.split('?')[0] || '');

  if (companyIdsForPath.length === 1 && String(companyIdsForPath[0]).trim()) {
    const enc = encodeOpaqueUrlPathSegment(String(companyIdsForPath[0]).trim());
    if (!hasCompanyInJobsPath) {
      path += `/${jobsCompanySegment}/${enc}`;
    } else {
      const stripped = path.replace(/\/(?:employer|tyonantaja|arbetsgivare|company|yritys|foretag)\/[^/?]+/i, '');
      path = `${stripped}/${jobsCompanySegment}/${enc}`;
    }
  } else if (hasCompanyInJobsPath) {
    path = path.replace(/\/(?:employer|tyonantaja|arbetsgivare|company|yritys|foretag)\/[^/?]+/i, '');
  }

  // Category/role in path: when the current `field` refinement is exactly what the path
  // segment already encodes, keep the pretty path and omit redundant ?field= params —
  // otherwise pagination links on hub pages become noindexed query URLs. When the field
  // state diverges from the segment (user changed filters), strip the segment so the
  // query params are authoritative.
  let fieldEncodedInPath = false;
  const fieldValuesForPath = (indexUiState.refinementList?.field || []).filter(
    (v: unknown) => v != null && String(v).trim() !== '',
  );
  const pathOnlyForSegments = path.split('?')[0] || '';
  const categorySegmentMatch = pathOnlyForSegments.match(/\/(?:category|kategoria|kategori)\/([^/?]+)/i);
  const roleSegmentMatch = pathOnlyForSegments.match(/\/(?:role|ammatti|yrke)\/([^/?]+)/i);
  if (categorySegmentMatch) {
    const category = getCategoryBySlug(decodeURIComponent(categorySegmentMatch[1]));
    const expected = (category ? getCategoryFieldValuesForLocale(category, locale) : [])
      .map((v) => v.toLowerCase())
      .sort();
    const actual = fieldValuesForPath.map((v: string) => String(v).toLowerCase()).sort();
    if (category && expected.length === actual.length && expected.every((v, idx) => v === actual[idx])) {
      fieldEncodedInPath = true;
    } else {
      path = path.replace(/\/(?:category|kategoria|kategori)\/[^/?]+/i, '');
    }
  }
  // Role in path: same rule as category, but against the `normalisedTitle` refinement.
  let titleEncodedInPath = false;
  if (roleSegmentMatch) {
    const titleValuesForPath = (indexUiState.refinementList?.normalisedTitle || []).filter(
      (v: unknown) => v != null && String(v).trim() !== '',
    );
    const roleDef = getRoleBySlug(decodeURIComponent(roleSegmentMatch[1]));
    if (roleDef && titleValuesMatchRole(roleDef, locale, titleValuesForPath.map(String))) {
      titleEncodedInPath = true;
    } else {
      path = path.replace(/\/(?:role|ammatti|yrke)\/[^/?]+/i, '');
    }
  }

  // Freelance hub (/jobs/freelance, fi: keikkatyo, sv: frilans) — same "keep the pretty path
  // only while it still matches the current filter state" rule as category/role above. Never
  // proactively rewrites a manually-checked jobType=Freelance filter into this path — only a
  // page that was already on the pretty path keeps it, same as category/role's own precedent.
  let jobTypeEncodedInPath = false;
  const jobTypeValuesForPath = (indexUiState.refinementList?.jobType || []).filter(
    (v: unknown) => v != null && String(v).trim() !== '',
  );
  const freelanceSegmentMatch = pathOnlyForSegments.match(/\/(?:freelance|keikkatyo|frilans)$/i);
  if (freelanceSegmentMatch) {
    const expectedFreelance = freelanceJobTypeFacetForLocale(locale).toLowerCase();
    const actualJobType = jobTypeValuesForPath.map((v: string) => String(v).toLowerCase());
    if (actualJobType.length === 1 && actualJobType[0] === expectedFreelance) {
      jobTypeEncodedInPath = true;
    } else {
      path = path.replace(/\/(?:freelance|keikkatyo|frilans)$/i, '');
    }
  }

  // Add query if present
  if (indexUiState.query) params.set('search', indexUiState.query);

  // Add refinementList parameters
  if (indexUiState.refinementList) {
    for (const [attribute, values] of Object.entries(indexUiState.refinementList)) {
      if (attribute === 'location') continue; // already encoded in path
      if (attribute === 'companyId') continue; // encoded in path (or employer profile URL)
      if (attribute === 'field' && fieldEncodedInPath) continue; // encoded as /category/… path segment
      if (attribute === 'normalisedTitle' && titleEncodedInPath) continue; // encoded as /role/… path segment
      if (attribute === 'jobType' && jobTypeEncodedInPath) continue; // encoded as /freelance path segment
      if (attribute === 'remoteness' && remotenessEncodedInPath) continue; // encoded as /etatyo|/hybridityo path segment
      if (attribute === 'sort' || attribute.includes('[sort]')) {
        // Skip sort from refinementList - it's handled separately
        continue;
      }
      const list = Array.isArray(values) ? values : [values as any];
      // Filter out empty/null/undefined values but preserve valid ones (including 0 and negative numbers for timestamps)
      const validValues = list.filter((v) => v !== undefined && v !== null && String(v).trim() !== '');
      if (validValues.length === 0) {
        continue;
      }
      validValues.forEach((value) => {
        const raw = String(value);
        // Lowercase most facet values for stable URLs; company IDs are opaque — preserve casing/symbols.
        const encoded =
          attribute.toLowerCase() === 'companyid' ? raw : raw.toLowerCase();
        params.append(attribute, encoded);
      });
    }
  }

  // Add sort parameter (omit if relevance to keep URL clean)
  if (indexUiState.sort && indexUiState.sort !== 'relevance') {
    params.set('sort', indexUiState.sort);
  }

  // Keep page explicit for non-first pages so pagination links are stable and predictable.
  // 0-based: page 0 = first page (omit), page 1 = second page, etc.
  if (indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0) {
    params.set('page', indexUiState.page.toString());
  }

  // Results-per-page: only put in URL when it differs from the default so default-page URLs stay clean
  if (
    indexUiState.perPage !== undefined &&
    indexUiState.perPage !== null &&
    indexUiState.perPage !== SRP_HITS_PER_PAGE
  ) {
    params.set('perPage', indexUiState.perPage.toString());
  }

  // Finland filter: only put in URL when false so default (true) stays out of the query string
  if (indexUiState.isFinland === false) {
    params.set('finland', '0');
  }

  // View mode: only put in URL when 'map' so default (list) URLs stay clean
  if (indexUiState.view === 'map') {
    params.set('view', 'map');
  }

  const queryString = params.toString();
  const result = queryString ? `${path}?${queryString}` : path;
  
  return result;
}

/**
 * Maps a path to its corresponding route key
 * @param path The path to map (without language prefix)
 * @returns The corresponding route key or null if not found
 */
function mapPathToRouteKey(path: string): string | null {
  // Handle dynamic routes
  for (const lang of ['en', 'fi', 'sv'] as const) {
    if (path.startsWith(`/${LOCALIZED_PATHS[lang].job}/`)) {
      return 'route.jobDetailPage';
    }
  }

  // Try to find a matching route key in any language
  for (const lang of ['en', 'fi', 'sv'] as const) {
    const langMessages = messages[lang];
    const routeKey = Object.entries(langMessages).find(([key, value]) => key.startsWith('route.') && value === path)?.[0];

    if (routeKey) {
      return routeKey;
    }
  }

  return null;
}

/**
 * Checks if the current pathname represents a search results page (SRP)
 * @param pathname The URL pathname
 * @param searchParams The URL search parameters
 * @returns true if this is an SRP, false otherwise
 */
function isSearchResultsPage(pathname: string, searchParams: URLSearchParams): boolean {
  // Check for search parameters that indicate an active search
  const hasSearchParams = 
    searchParams.has('search') ||
    searchParams.has('query') ||
    searchParams.has('en_search[query]') ||
    searchParams.has('fi_hae[query]') ||
    searchParams.has('sv_sok[query]') ||
    searchParams.has('sort') ||
    searchParams.has('page') ||
    searchParams.has('jobType') ||
    searchParams.has('isSummerJob') ||
    searchParams.has('remoteness') ||
    searchParams.has('location') ||
    searchParams.has('salaryRangeMin') ||
    searchParams.has('vacationWeeks') ||
    searchParams.has('companyHeadcountRangeMax') ||
    Array.from(searchParams.keys()).some(key => key.includes('[refinementList]'));

  // Check if pathname is home page or search page
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();
  const isLocale = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
  
  // Home page (root or locale-only)
  if (segments.length === 0 || (segments.length === 1 && isLocale)) {
    return true;
  }

  // Check for jobs listing paths (plural, not singular job detail pages)
  // Job detail uses singular: job/tyopaikka/jobb
  // Jobs listing uses plural: jobs/tyopaikat/lediga-jobb
  // Must sit right after the locale prefix (or be the first segment with no locale) — matching
  // at any position would also catch unrelated pages that merely contain a "jobs" segment
  // further down the path, e.g. /{locale}/juniors/jobs, which is its own page, not the SRP.
  const jobsSegmentIndex = isLocale ? 1 : 0;
  const isJobsListingPath = JOBS_LISTING_SEGMENTS.has(segments[jobsSegmentIndex] ?? '');

  if (isJobsListingPath) {
    // This is a jobs listing page (SRP)
    return true;
  }
  
  // Check if this is a job detail page (singular job path)
  // Job detail pattern: /{locale}/{job}/{jobId}/{companyId}/{seoSlug}
  const jobDetailPattern = /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)/;
  if (jobDetailPattern.test(pathname)) {
    return false; // Job detail page, not SRP
  }

  return hasSearchParams;
}

/**
 * Best-effort translation of the path segments that follow the jobs-listing segment (work
 * model, location, employer, category, and role facets/hubs, plus the bare roles-index and
 * freelance hubs) into their target-locale equivalents. Used by the language switcher so
 * `/fi/tyopaikat/ammatti/lahihoitaja` becomes `/sv/lediga-jobb/yrke/{samma-slug}` instead of
 * losing the role and landing on the bare listing. Anything it doesn't recognise (an ad-hoc
 * `field`/`normalisedTitle` free-text filter, an unmapped location) is silently dropped, since
 * carrying it over untranslated would 404 or return zero results in the target locale's index.
 */
function translateJobsListingTail(tailSegments: string[], targetLocale: 'en' | 'fi' | 'sv'): string[] {
  if (tailSegments.length === 1 && ROLES_INDEX_SEGMENTS.has(tailSegments[0].toLowerCase())) {
    return [LOCALIZED_PATHS[targetLocale].rolesIndex];
  }
  if (tailSegments.length === 1 && FREELANCE_PATH_MARKERS.has(tailSegments[0].toLowerCase())) {
    return [FREELANCE_PATH_MARKER_BY_LOCALE[targetLocale]];
  }

  const out: string[] = [];
  let cursor = 0;

  const workModel = workModelForSegment(tailSegments[cursor]);
  if (workModel) {
    out.push(workModelSegment(workModel, targetLocale));
    cursor += 1;
  }

  if (LOCATION_PATH_MARKERS.has((tailSegments[cursor] ?? '').toLowerCase())) {
    const rawLocationSlug = tailSegments[cursor + 1];
    if (rawLocationSlug) {
      const translated = rawLocationSlug
        .split('+')
        .map((part) => buildEncodedLocationSrpPathSegment(part, targetLocale))
        .filter(Boolean)
        .join('+');
      if (translated) {
        out.push(LOCATION_PATH_MARKER_BY_LOCALE[targetLocale], translated);
      }
    }
    cursor += rawLocationSlug ? 2 : 1;
  }

  const companyMarkers = [...Object.values(JOBS_COMPANY_PATH_SEGMENT), ...JOBS_COMPANY_LEGACY_PATH_SEGMENTS];
  if (companyMarkers.includes((tailSegments[cursor] ?? '').toLowerCase())) {
    const companySlug = tailSegments[cursor + 1];
    if (companySlug) {
      out.push(JOBS_COMPANY_PATH_SEGMENT[targetLocale], companySlug);
    }
    cursor += companySlug ? 2 : 1;
  }

  if (CATEGORY_PATH_MARKERS.has((tailSegments[cursor] ?? '').toLowerCase())) {
    const categorySlug = tailSegments[cursor + 1];
    const category = categorySlug ? getCategoryBySlug(decodeURIComponent(categorySlug)) : null;
    if (category) {
      out.push(CATEGORY_PATH_MARKER_BY_LOCALE[targetLocale], category.slug);
    }
    cursor += categorySlug ? 2 : 1;
  }

  if (isRolePathMarker(tailSegments[cursor] ?? '')) {
    const roleSlug = tailSegments[cursor + 1];
    const role = roleSlug ? getRoleBySlug(decodeURIComponent(roleSlug)) : null;
    if (role) {
      out.push(ROLE_PATH_MARKER_BY_LOCALE[targetLocale], role.slug);
    }
    cursor += roleSlug ? 2 : 1;
  }

  return out;
}

/**
 * Converts a URL pathname to the same route in a target language.
 * Accepts either a pathname (e.g. /sv/jobs) or a full URL (e.g. http://localhost:3000/sv/jobs); full URLs are normalized to pathname + search.
 *
 * @param pathnameOrUrl The current URL pathname, or full URL (pathname may include search parameters)
 * @param targetLanguage The target language code (e.g., 'en', 'fi', 'sv')
 * @param intl The IntlShape object from react-intl
 * @returns The same page in the target language (path only), or home page for SRPs
 */
export function convertUrlToLanguage(pathnameOrUrl: string, targetLanguage: string, intl: IntlShape): string {
  let pathname = pathnameOrUrl;
  if (pathnameOrUrl.includes('://')) {
    try {
      const url = new URL(pathnameOrUrl);
      pathname = url.pathname + (url.search ? url.search : '');
    } catch {
      pathname = pathnameOrUrl;
    }
  }

  // Split pathname and search parameters
  const [cleanPathname, searchParamsString] = pathname.split('?');
  const searchParams = new URLSearchParams(searchParamsString || '');
  
  // Check if this is a search results page (SRP)
  if (isSearchResultsPage(cleanPathname, searchParams)) {
    const srpSegments = cleanPathname.split('/').filter(Boolean);
    const srpFirstSegment = srpSegments[0]?.toLowerCase();
    const isHomePage =
      srpSegments.length === 0 ||
      (srpSegments.length === 1 && srpFirstSegment && ['en', 'fi', 'sv'].includes(srpFirstSegment));

    if (isHomePage) {
      return `/${targetLanguage}`;
    }

    // Free-text queries and ad-hoc query-param filters can't be reliably translated across
    // locales, so those are dropped. Path-encoded facets/hubs (work model, location, employer,
    // category, role, roles-index, freelance) DO have known per-locale equivalents, so translate
    // those rather than discarding them — landing on the target language's jobs listing page is
    // still the fallback for anything else, and far more useful than losing the search context
    // entirely by bouncing to the home page.
    const target = targetLanguage as 'en' | 'fi' | 'sv';
    const targetJobsSegment = JOBS_LISTING_SEGMENT[target];
    const srpJobsSegmentIndex = srpFirstSegment && ['en', 'fi', 'sv'].includes(srpFirstSegment) ? 1 : 0;
    const tailSegments = srpSegments.slice(srpJobsSegmentIndex + 1);
    const translatedTail = translateJobsListingTail(tailSegments, target);
    return [`/${targetLanguage}`, targetJobsSegment, ...translatedTail].join('/');
  }
  
  // Extract current locale and path without locale prefix
  const segments = cleanPathname.split("/").filter(Boolean);
  let currentLocale: 'en' | 'fi' | 'sv' = 'en';
  let pathWithoutLocale = cleanPathname;
  
  // Check if first segment is a locale
  const firstSegment = segments[0]?.toLowerCase();
  if (firstSegment && ['en', 'fi', 'sv'].includes(firstSegment)) {
    currentLocale = firstSegment as 'en' | 'fi' | 'sv';
    pathWithoutLocale = '/' + segments.slice(1).join('/');
    if (pathWithoutLocale === '/') {
      // Handle language home routes (e.g., "/en" -> "/fi")
      return `/${targetLanguage}`;
    }
  }
  
  // Handle job detail pages specially
  // Pattern: /{job}/{jobId}/{companyId}/{seoSlug}
  const jobSlugs = Object.values(LOCALIZED_PATHS).map((l) => l.job);
  const jobDetailPattern = new RegExp(`^/(${jobSlugs.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})/([^/]+)/([^/]+)(?:/(.+))?$`);
  const jobMatch = pathWithoutLocale.match(jobDetailPattern);
  
  if (jobMatch) {
    // Extract jobId, companyId, and seoSlug
    const [, , jobId, companyId, seoSlug] = jobMatch;
    const targetJobSlug = LOCALIZED_PATHS[targetLanguage as keyof typeof LOCALIZED_PATHS].job;
    
    // Preserve jobId and companyId, keep seoSlug as-is (it will be regenerated by the page if needed)
    const newPath = seoSlug 
      ? `/${targetLanguage}/${targetJobSlug}/${jobId}/${companyId}/${seoSlug}`
      : `/${targetLanguage}/${targetJobSlug}/${jobId}/${companyId}`;
    
    return searchParamsString ? `${newPath}?${searchParamsString}` : newPath;
  }
  
  // For static pages, use the route localization system
  // Convert localized path back to English equivalent
  const englishPath = delocalizeRoute(pathWithoutLocale, currentLocale);
  
  // Convert English path to target language
  const localizedPath = localizeRoute(englishPath, targetLanguage as 'en' | 'fi' | 'sv');
  
  // Add target language prefix (including for English)
  const newPath = `/${targetLanguage}${localizedPath}`;
  
  // Preserve any search parameters for static pages
  return searchParamsString ? `${newPath}?${searchParamsString}` : newPath;
}